import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../common/ConfirmModal";
import {
  formatNhsNumberDisplay,
  sanitizeNhsNumberInput,
  validateNhsNumber,
  validateNiNumber,
  validateOptionalNhsNumber,
} from "../../../utils/siteFieldValidationUtils";

export const ID_NUMBER_KINDS = {
  NHS: "nhs",
  NI: "ni",
};

const GUIDE = {
  [ID_NUMBER_KINDS.NHS]: {
    title: "NHS number",
    summary:
      "An NHS number uniquely identifies a patient in England, Wales and the Isle of Man.",
    formatTitle: "Format",
    formatLines: [
      "Exactly 10 digits",
      "Usually shown as 3–3–4 groups (e.g. 943 476 5919)",
      "Spaces and hyphens are ignored when saving",
    ],
    rulesTitle: "Validation",
    rulesLines: [
      "Must pass the NHS Modulus 11 checksum",
      "Invalid or incomplete numbers cannot be saved",
    ],
    example: "943 476 5919",
  },
  [ID_NUMBER_KINDS.NI]: {
    title: "National Insurance (NI) number",
    summary:
      "A UK National Insurance number identifies an individual for tax and National Insurance contributions.",
    formatTitle: "Format",
    formatLines: [
      "2 letters + 6 digits + 1 letter (A–D)",
      "Example: AB 12 34 56 C",
      "Spaces are optional; letters are stored in uppercase",
    ],
    rulesTitle: "Validation",
    rulesLines: [
      "First letter cannot be D, F, I, Q, U or V",
      "Second letter cannot be D, F, I, O, Q, U or V",
      "Prefixes GB, BG, NK, KN, TN, NT and ZZ are not allowed",
    ],
    example: "AB123456C",
  },
};

export function getIdNumberGuide(kind) {
  return GUIDE[kind] || null;
}

export function formatNhsForDisplay(value) {
  return formatNhsNumberDisplay(value);
}

export function formatNiForDisplay(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase();
}

export function getNhsVerifyStatusHint(value, isVerified) {
  const check = validateOptionalNhsNumber(value);
  if (!check.valid || !check.digits) return "";
  return Number(isVerified) ? "Marked as verified" : "Not verified";
}

export function getNiVerifyStatusHint(value, isVerified) {
  const check = validateNiNumber(value);
  if (!check.valid || !check.ni) return "";
  return Number(isVerified) ? "Marked as verified" : "Not verified";
}

export function getNhsDisplayWithVerify(value, isVerified) {
  const digits = sanitizeNhsNumberInput(value);
  if (!digits) return "Not recorded";
  const formatted = formatNhsNumberDisplay(digits);
  return Number(isVerified) ? `${formatted} (Verified)` : formatted;
}

export function getNiDisplayWithVerify(value, isVerified) {
  const raw = formatNiForDisplay(value);
  if (!raw) return "Not recorded";
  return Number(isVerified) ? `${raw} (Verified)` : raw;
}

/**
 * Promise-based Yes/No verify prompt shared by Add + Profile.
 */
export function useIdVerifyPrompt() {
  const [prompt, setPrompt] = useState({
    open: false,
    title: "",
    message: "",
  });
  const resolverRef = useRef(null);
  const promptedNhsRef = useRef("");
  const promptedNiRef = useRef("");

  const resetPrompted = useCallback(() => {
    promptedNhsRef.current = "";
    promptedNiRef.current = "";
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    setPrompt({ open: false, title: "", message: "" });
  }, []);

  const askVerified = useCallback((title, message) => {
    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
      setPrompt({ open: true, title, message });
    });
  }, []);

  const closePrompt = useCallback((verified) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPrompt({ open: false, title: "", message: "" });
    if (resolve) resolve(Boolean(verified));
  }, []);

  const ensureNhsVerified = useCallback(
    async (value, currentIsVerifyNHS = 0) => {
      const check = validateOptionalNhsNumber(value);
      if (!check.valid) {
        return { ok: false, message: check.message, IsVerifyNHS: 0, digits: "" };
      }
      if (!check.digits) {
        promptedNhsRef.current = "";
        return { ok: true, IsVerifyNHS: 0, digits: "" };
      }
      if (promptedNhsRef.current === check.digits) {
        return {
          ok: true,
          IsVerifyNHS: Number(currentIsVerifyNHS) ? 1 : 0,
          digits: check.digits,
        };
      }
      const verified = await askVerified(
        "Verify NHS number",
        "Has this NHS number been verified?",
      );
      promptedNhsRef.current = check.digits;
      return {
        ok: true,
        IsVerifyNHS: verified ? 1 : 0,
        digits: check.digits,
      };
    },
    [askVerified],
  );

  const ensureNiVerified = useCallback(
    async (value, currentIsVerify = 0) => {
      const check = validateNiNumber(value);
      if (!check.valid) {
        return { ok: false, message: check.message, IsVerify: 0, ni: "" };
      }
      if (!check.ni) {
        promptedNiRef.current = "";
        return { ok: true, IsVerify: 0, ni: "" };
      }
      if (promptedNiRef.current === check.ni) {
        return {
          ok: true,
          IsVerify: Number(currentIsVerify) ? 1 : 0,
          ni: check.ni,
        };
      }
      const verified = await askVerified(
        "Verify NI number",
        "Has this NI number been verified?",
      );
      promptedNiRef.current = check.ni;
      return {
        ok: true,
        IsVerify: verified ? 1 : 0,
        ni: check.ni,
      };
    },
    [askVerified],
  );

  const clearNhsPrompted = useCallback(() => {
    promptedNhsRef.current = "";
  }, []);

  const clearNiPrompted = useCallback(() => {
    promptedNiRef.current = "";
  }, []);

  const VerifyModal = (
    <ConfirmModal
      isOpen={prompt.open}
      onClose={() => closePrompt(false)}
      onConfirm={() => closePrompt(true)}
      title={prompt.title || "Verify"}
      message={prompt.message}
      confirmText="Yes"
      cancelText="No"
      confirmButtonClass="btn-primary"
      icon="fas fa-shield-alt text-primary"
    />
  );

  return {
    VerifyModal,
    resetPrompted,
    ensureNhsVerified,
    ensureNiVerified,
    clearNhsPrompted,
    clearNiPrompted,
    askVerified,
  };
}

export function IdNumberGuideModal({ kind, open, onClose }) {
  const guide = getIdNumberGuide(kind);
  if (!open || !guide) return null;

  return createPortal(
    <div
      className="pm-id-guide-modal"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="pm-id-guide-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-id-guide-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-id-guide-modal__header">
          <h3 id="pm-id-guide-title" className="pm-id-guide-modal__title">
            <i className="fas fa-info-circle me-2" aria-hidden="true" />
            {guide.title}
          </h3>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
        <div className="pm-id-guide-modal__body">
          <p className="pm-id-guide-modal__summary">{guide.summary}</p>
          <h4 className="pm-id-guide-modal__section">{guide.formatTitle}</h4>
          <ul className="pm-id-guide-modal__list">
            {guide.formatLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <h4 className="pm-id-guide-modal__section">{guide.rulesTitle}</h4>
          <ul className="pm-id-guide-modal__list">
            {guide.rulesLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="pm-id-guide-modal__example">
            <span className="pm-id-guide-modal__example-label">Example</span>
            <code>{guide.example}</code>
          </div>
        </div>
        <div className="pm-id-guide-modal__footer">
          <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Label text + info icon that opens the NHS/NI guide modal.
 */
export function IdNumberLabel({
  kind,
  children,
  required = false,
  className = "",
}) {
  const [guideOpen, setGuideOpen] = useState(false);
  const guide = getIdNumberGuide(kind);
  const title = guide?.title || String(children || "");

  return (
    <>
      <span className={`pm-id-label ${className}`.trim()}>
        <span className="pm-id-label__text">{children || title}</span>
        {required ? <span className="pm-add-field__req">*</span> : null}
        <button
          type="button"
          className="pm-id-label__info"
          title={`About ${title}`}
          aria-label={`About ${title}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setGuideOpen(true);
          }}
        >
          <i className="fas fa-info-circle" aria-hidden="true" />
        </button>
      </span>
      <IdNumberGuideModal
        kind={kind}
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
    </>
  );
}

/** Validate NHS on blur; returns { error, flags } without prompting. */
export function validateNhsOnBlur(value) {
  if (!String(value || "").trim()) {
    return { error: "", IsVerifyNHS: 0, valid: true, empty: true };
  }
  const check = validateNhsNumber(value);
  if (!check.valid) {
    return {
      error: check.message,
      IsVerifyNHS: 0,
      valid: false,
      empty: false,
    };
  }
  return {
    error: "",
    IsVerifyNHS: 0,
    valid: true,
    empty: false,
    digits: check.digits,
  };
}

export function validateNiOnBlur(value) {
  if (!String(value || "").trim()) {
    return { error: "", IsVerify: 0, valid: true, empty: true };
  }
  const check = validateNiNumber(value);
  if (!check.valid) {
    return {
      error: check.message,
      IsVerify: 0,
      valid: false,
      empty: false,
    };
  }
  return {
    error: "",
    IsVerify: 0,
    valid: true,
    empty: false,
    ni: check.ni,
  };
}
